import {
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Tooltip,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { useEffect, useMemo, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { AppModal, bodyTextSx } from "global/components/AppModal";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import {
  handleErrorMessage,
  openErrorNotification,
  openSuccessNotification,
} from "helpers/methods";
import urls from "global/constants/UrlConstants";
import strings from "global/constants/StringConstants";
import { useAppSelector } from "hooks/use-app-select";
import { selectAccount } from "redux/authSlice";
import {
  fetchSsoConnections,
  deleteSsoConnector,
  downloadSamlMetadata,
} from "./SsoConfigService";
import { SsoConnection, SsoStatus } from "./SsoConfigTypes";
import AddSsoConnectorModal from "./AddSsoConnectorModal";
import SettingsLayout from "screens/Settings/components/SettingsLayout";
import AttributeMappingPanel from "./AttributeMappingPanel";
import {
  actionButtonSx,
  actionsRowSx,
  cardSx,
  connectorCardChipRowSx,
  connectorCardGroupSx,
  connectorCardHeadSx,
  connectorCardLabelSx,
  connectorCardSubSx,
  connectorCardSx,
  connectorCardTitleSx,
  columnHeaderSx,
  connectorNameSx,
  connectorSubTextSx,
  emptyStateSx,
  neutralChipSx,
  pageSubtitleSx,
  pageTitleSx,
  planBlockedDescSx,
  planBlockedIconSx,
  planBlockedTitleSx,
  primaryButtonSx,
  rowSx,
  sourceTextSx,
  statusChipSx,
  tableInnerSx,
  tableScrollSx,
  valueLabelSx,
  valueTextSx,
} from "./EnterpriseSSO.styles";
import EmptyState from "global/components/EmptyState/EmptyState";

const COLUMNS = [
  "CONNECTOR",
  "PROTOCOL",
  "EMAIL DOMAINS",
  "IDP SOURCE",
  "LOGIN POLICY",
  "STATUS",
];

const STATUS_LABEL: Record<SsoStatus, string> = {
  Draft: "Setup incomplete",
  Active: "Live",
  Inactive: "Paused",
};

const isCode = (error: any, code: number) =>
  error?.code === code || error?.status === code || error?.statusCode === code;

const isOidcConfig = (config: SsoConnection | null | undefined) =>
  (config?.protocol ?? "SAML") === "OIDC";

const EnterpriseSSO = () => {
  const account = useAppSelector(selectAccount);
  const [configs, setConfigs] = useState<SsoConnection[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isApiLoading, setIsApiLoading] = useState<boolean>(false);
  const [planBlocked, setPlanBlocked] = useState<boolean>(false);
  // The seven-column table cannot fit a phone or an 834 tablet; below `md`
  // every connector renders as its own card instead.
  const isCompact = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editConfig, setEditConfig] = useState<SsoConnection | null>(null);

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuConfig, setMenuConfig] = useState<SsoConnection | null>(null);
  const [metadataDownloading, setMetadataDownloading] = useState(false);

  const [isDeleteOpen, setIsDeleteOpen] = useState<boolean>(false);
  const [selectedConfig, setSelectedConfig] = useState<SsoConnection | null>(
    null,
  );

  const [isMappingOpen, setIsMappingOpen] = useState<boolean>(false);
  const [mappingConfig, setMappingConfig] = useState<SsoConnection | null>(null);

  const [isSpInfoOpen, setIsSpInfoOpen] = useState<boolean>(false);
  const [spInfoConfig, setSpInfoConfig] = useState<SsoConnection | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const fetchAll = async () => {
    setIsLoading(true);
    try {
      const data = await fetchSsoConnections();
      setConfigs(Array.isArray(data) ? data : []);
      setPlanBlocked(false);
    } catch (error: any) {
      if (isCode(error, 403)) {
        setPlanBlocked(true);
        setConfigs([]);
      } else {
        handleErrorMessage(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAddModal = () => {
    setEditConfig(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditConfig(null);
  };

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    config: SsoConnection,
  ) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuConfig(config);
  };

  const handleCloseMenu = () => setMenuAnchorEl(null);

  const handleMenuItemClick = (
    action:
      | "edit"
      | "delete"
      | "test"
      | "sp-info"
      | "download-metadata"
      | "attribute-mapping",
  ) => {
    const config = menuConfig;
    setMenuAnchorEl(null);
    if (!config) return;
    if (action === "edit") {
      setEditConfig(config);
      setIsModalOpen(true);
    } else if (action === "delete") {
      setSelectedConfig(config);
      setIsDeleteOpen(true);
    } else if (action === "attribute-mapping") {
      setMappingConfig(config);
      setIsMappingOpen(true);
    } else if (action === "sp-info") {
      setSpInfoConfig(config);
      setIsSpInfoOpen(true);
    } else if (action === "download-metadata") {
      handleDownloadMetadata(config);
    } else {
      testConfig(config);
    }
  };

  const handleDownloadMetadata = async (config: SsoConnection) => {
    try {
      setMetadataDownloading(true);
      const blob = await downloadSamlMetadata(config.registrationId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${config.slug || config.registrationId}-sp-metadata.xml`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setMetadataDownloading(false);
    }
  };

  const testConfig = (config: SsoConnection) => {
    if (config.status !== "Active") {
      openErrorNotification(
        "Complete setup and activate this connector before testing.",
      );
      return;
    }
    const domain = config.emailDomains?.[0];
    if (!domain) {
      openErrorNotification("Add an email domain before testing.");
      return;
    }
    const url = `${urls.SAML_LOGIN_INIT}?email=${encodeURIComponent(
      `test@${domain}`,
    )}&app=${encodeURIComponent(strings.inkfree)}`;
    openSuccessNotification(
      "SSO test opened in a new tab — your browser will redirect to your IdP login page.",
    );
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleDelete = async (config: SsoConnection) => {
    try {
      setIsApiLoading(true);
      await deleteSsoConnector(config.registrationId);
      openSuccessNotification(
        `Deleted SSO configuration '${config.registrationId}'.`,
      );
      setIsDeleteOpen(false);
      await fetchAll();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsApiLoading(false);
    }
  };

  const moreOptions = () => (
    <Menu
      id="saml-config-actions-menu"
      anchorEl={menuAnchorEl}
      keepMounted
      open={Boolean(menuAnchorEl)}
      onClose={handleCloseMenu}
      role="menu"
    >
      <MenuItem onClick={() => handleMenuItemClick("sp-info")}>
        {isOidcConfig(menuConfig) ? "RP Setup Info" : "SP Setup Info"}
      </MenuItem>
      {menuConfig?.status === "Active" && !isOidcConfig(menuConfig) && (
        <MenuItem
          onClick={() => handleMenuItemClick("download-metadata")}
          disabled={metadataDownloading}
        >
          {metadataDownloading ? "Downloading..." : "Download metadata.xml"}
        </MenuItem>
      )}
      <MenuItem
        onClick={() => handleMenuItemClick("test")}
        disabled={menuConfig?.status !== "Active"}
      >
        Test SSO
      </MenuItem>
      <MenuItem onClick={() => handleMenuItemClick("edit")}>
        {menuConfig?.status === "Draft" ? "Complete setup" : "Edit"}
      </MenuItem>
      <MenuItem onClick={() => handleMenuItemClick("attribute-mapping")}>
        Attribute mapping
      </MenuItem>
      <MenuItem
        onClick={() => handleMenuItemClick("delete")}
        sx={{ color: "error.main" }}
      >
        Delete
      </MenuItem>
    </Menu>
  );

  const getPlanBlocked = () => (
    <Box sx={cardSx}>
      <Stack alignItems="center" spacing={1.5} sx={{ py: 6, px: 3 }}>
        <Box sx={planBlockedIconSx}>
          <LockOutlinedIcon />
        </Box>
        <Typography sx={planBlockedTitleSx}>
          Enterprise SSO isn't included in your plan
        </Typography>
        <Typography sx={planBlockedDescSx} textAlign="center" maxWidth="420px">
          Upgrade to a plan that includes single sign-on (SAML / OIDC) to
          configure your identity provider. Contact your account team to enable
          it.
        </Typography>
      </Stack>
    </Box>
  );

  /** Compact record used below `md`, where the seven columns cannot fit. */
  const getConnectorCard = (config: SsoConnection) => {
    const domains = config.emailDomains ?? [];
    const policies = [
      config.autoProvisionUsers ? "Auto-onboard" : null,
      config.sendLoginHint ? "Login hint" : null,
    ].filter(Boolean) as string[];

    return (
      <Box key={config.registrationId} sx={connectorCardSx}>
        <Box sx={connectorCardHeadSx}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography component="div" sx={connectorCardTitleSx}>
              {config.slug || config.registrationId}
            </Typography>
            <Typography component="div" sx={connectorCardSubSx}>
              {config.registrationId}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={(e) => handleOpenMenu(e, config)}
            aria-label={`More actions for ${config.registrationId}`}
            aria-haspopup="true"
            sx={actionButtonSx}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={connectorCardChipRowSx}>
          <Chip
            size="small"
            label={isOidcConfig(config) ? "OIDC" : "SAML"}
            sx={neutralChipSx}
          />
          <Chip
            size="small"
            label={STATUS_LABEL[config.status]}
            sx={statusChipSx(config.status)}
          />
          <Chip size="small" label={getIdpSource(config)} sx={neutralChipSx} />
        </Box>

        {domains.length > 0 && (
          <Box sx={connectorCardGroupSx}>
            <Box component="span" sx={connectorCardLabelSx}>
              Email domains
            </Box>
            <Box sx={connectorCardChipRowSx}>
              {domains.map((d) => (
                <Chip key={d} label={d} size="small" sx={neutralChipSx} />
              ))}
            </Box>
          </Box>
        )}

        {policies.length > 0 && (
          <Box sx={connectorCardGroupSx}>
            <Box component="span" sx={connectorCardLabelSx}>
              Login policy
            </Box>
            <Box sx={connectorCardChipRowSx}>
              {policies.map((label) => (
                <Chip
                  key={label}
                  size="small"
                  label={label}
                  sx={neutralChipSx}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const getIdpSource = (config: SsoConnection) =>
    isOidcConfig(config)
      ? config.oidc?.issuer
        ? "Issuer URL"
        : config.oidc?.authorizationUri
          ? "Manual"
          : "—"
      : config.saml?.metadataUrl
        ? "Metadata URL"
        : config.saml?.idpEntityId
          ? "Manual"
          : "—";

  const getRow = (config: SsoConnection, index: number) => (
    <Box key={config.registrationId} sx={rowSx(index === configs.length - 1)}>
      <Box component="span" sx={{ minWidth: 0 }}>
        <Typography component="div" noWrap sx={connectorNameSx}>
          {config.slug || config.registrationId}
        </Typography>
        <Typography component="div" noWrap sx={connectorSubTextSx}>
          {config.registrationId}
        </Typography>
      </Box>

      <Box component="span">
        <Chip
          size="small"
          label={isOidcConfig(config) ? "OIDC" : "SAML"}
          sx={neutralChipSx}
        />
      </Box>

      <Box
        component="span"
        sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
      >
        {(config.emailDomains ?? []).slice(0, 3).map((d) => (
          <Chip key={d} label={d} size="small" sx={neutralChipSx} />
        ))}
        {(config.emailDomains?.length ?? 0) > 3 && (
          <Chip
            label={`+${config.emailDomains.length - 3}`}
            size="small"
            sx={neutralChipSx}
          />
        )}
      </Box>

      <Box component="span" sx={sourceTextSx}>
        {getIdpSource(config)}
      </Box>

      <Box
        component="span"
        sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
      >
        {config.autoProvisionUsers && (
          <Chip size="small" label="Auto-onboard" sx={neutralChipSx} />
        )}
        {config.sendLoginHint && (
          <Chip size="small" label="Login hint" sx={neutralChipSx} />
        )}
      </Box>

      <Box component="span">
        <Chip
          size="small"
          label={STATUS_LABEL[config.status]}
          sx={statusChipSx(config.status)}
        />
      </Box>

      <Box component="span" sx={{ display: "flex", justifyContent: "end" }}>
        <IconButton
          size="small"
          onClick={(e) => handleOpenMenu(e, config)}
          aria-label={`More actions for ${config.registrationId}`}
          aria-haspopup="true"
          sx={actionButtonSx}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );

  const getLoadingRow = () => (
    <Box sx={rowSx(true)}>
      <Skeleton variant="text" width="70%" height={18} />
      <Skeleton variant="text" width="50%" height={18} />
      <Skeleton variant="text" width="60%" height={18} />
      <Skeleton variant="text" width="50%" height={18} />
      <Skeleton variant="text" width="60%" height={18} />
      <Skeleton variant="text" width="70%" height={18} />
      <Box />
    </Box>
  );

  const rows = useMemo(
    () => configs.map((config, index) => getRow(config, index)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [configs],
  );

  const isProtocolAtCap = (protocol: "SAML" | "OIDC") =>
    configs.filter((c) => (c.protocol ?? "SAML") === protocol).length >= 1;
  const bothProtocolsAtCap = isProtocolAtCap("SAML") && isProtocolAtCap("OIDC");

  /*
    Sits in `SettingsLayout`'s `headerAction` slot rather than in a row of its
    own, so the screen opens on the same title-plus-action line as Roles and
    Teams instead of pushing its first card a row lower than every sibling.
  */
  const addConnectorAction = planBlocked ? undefined : (
    <Box sx={actionsRowSx}>
      <Tooltip
        title={
          bothProtocolsAtCap
            ? "Only one connector per protocol (SAML and OIDC) is supported per account. Delete an existing connector to add a different one."
            : ""
        }
        describeChild
      >
        <span>
          <Button
            id="btn_add_saml_config"
            variant="contained"
            disabled={isLoading || bothProtocolsAtCap}
            startIcon={<AddIcon />}
            onClick={openAddModal}
            sx={primaryButtonSx}
          >
            Add Connector
          </Button>
        </span>
      </Tooltip>
    </Box>
  );

  /*
    A node rather than a plain string so the protocols can sit under the name as
    a subtitle instead of running on in the heading itself. `SettingsLayout`
    still drops the whole block on the phone, where the app bar carries the
    screen name.
  */
  const titleNode = (
    <Box>
      <Typography component="h1" sx={pageTitleSx}>
        Enterprise SSO
      </Typography>
      <Typography sx={pageSubtitleSx}>SAML 2.0 / OIDC</Typography>
    </Box>
  );

  return (
    <SettingsLayout
      activeTab="Enterprise SSO"
      title={titleNode}
      headerAction={addConnectorAction}
    >
      <Stack direction="column" spacing={2.5}>
        {planBlocked ? (
          getPlanBlocked()
        ) : (
          <Box
            sx={cardSx}
            component="section"
            aria-label="Enterprise SSO configurations"
          >
            {isCompact ? (
              isLoading ? (
                getLoadingRow()
              ) : configs.length > 0 ? (
                configs.map(getConnectorCard)
              ) : (
                <EmptyState
                  icon="link"
                  title="No SSO connectors yet"
                  body="Add a SAML 2.0 or OIDC connector to let your team sign in with your identity provider."
                  primaryAction={
                    bothProtocolsAtCap
                      ? undefined
                      : {
                          label: "Add Connector",
                          onClick: openAddModal,
                          disabled: isLoading,
                        }
                  }
                />
              )
            ) : (
              <Box sx={tableScrollSx}>
                <Box sx={tableInnerSx}>
                  <Box sx={columnHeaderSx}>
                    {COLUMNS.map((column) => (
                      <Box component="span" key={column}>
                        {column}
                      </Box>
                    ))}
                    <Box component="span" />
                  </Box>

                  {isLoading ? (
                    getLoadingRow()
                  ) : configs.length > 0 ? (
                    rows
                  ) : (
                    <EmptyState
                      icon="link"
                      title="No SSO connectors yet"
                      body="Add a SAML 2.0 or OIDC connector to let your team sign in with your identity provider."
                      primaryAction={
                        bothProtocolsAtCap
                          ? undefined
                          : {
                              label: "Add Connector",
                              onClick: openAddModal,
                              disabled: isLoading,
                            }
                      }
                    />
                  )}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {moreOptions()}

        {isModalOpen && (
          <AddSsoConnectorModal
            isVisible={isModalOpen}
            handleDialogClose={closeModal}
            onSuccess={fetchAll}
            editConfig={editConfig}
            defaultSlug={`${strings.inkfree}-${account}`}
            existingConfigs={configs}
          />
        )}

        {isMappingOpen && mappingConfig && (
          <AttributeMappingPanel
            open={isMappingOpen}
            connection={mappingConfig}
            onClose={() => {
              setIsMappingOpen(false);
              setMappingConfig(null);
            }}
            onSaved={fetchAll}
          />
        )}

        {isDeleteOpen && selectedConfig && (
          <AppModal
            open={isDeleteOpen}
            onClose={() => setIsDeleteOpen(false)}
            title="Please confirm"
            tone="destructive"
            size="sm"
            busy={isApiLoading}
            primaryLabel="Delete"
            onPrimary={() => handleDelete(selectedConfig)}
            primaryLoading={isApiLoading}
            cancelLabel="Cancel"
            onCancel={() => setIsDeleteOpen(false)}
          >
            <Typography sx={bodyTextSx}>
              {"Do you really want to delete the SSO configuration "}
              <span style={{ fontWeight: 700 }}>
                {selectedConfig.registrationId}
              </span>
              {
                "? Users on its domains will no longer be able to sign in via SSO."
              }
            </Typography>
          </AppModal>
        )}

        {isSpInfoOpen && spInfoConfig && (
          <AppModal
            open={isSpInfoOpen}
            onClose={() => {
              setIsSpInfoOpen(false);
              setCopiedField(null);
            }}
            title={`${
              isOidcConfig(spInfoConfig) ? "RP Setup Info" : "SP Setup Info"
            } — ${spInfoConfig.registrationId}`}
            tone="neutral"
            size="md"
            primaryLabel="Close"
            onPrimary={() => {
              setIsSpInfoOpen(false);
              setCopiedField(null);
            }}
            hideCancel
          >
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                {isOidcConfig(spInfoConfig)
                  ? "Enter these values in your identity provider (IdP) to complete the OIDC / OAuth 2.0 configuration."
                  : "Enter these values in your identity provider (IdP) to complete the SAML configuration."}
              </Typography>
              {(isOidcConfig(spInfoConfig)
                ? [
                    {
                      key: "redirect",
                      label: "Redirect URI / Callback URL",
                      value:
                        spInfoConfig.oidc?.rp?.redirectUri ??
                        `${urls.OIDC_REDIRECT_URI_BASE}/${spInfoConfig.registrationId}`,
                    },
                    {
                      key: "postLogout",
                      label: "Post-Logout Redirect URI",
                      value:
                        spInfoConfig.oidc?.rp?.postLogoutRedirectUri ??
                        urls.OIDC_POST_LOGOUT_REDIRECT_URI,
                    },
                    {
                      key: "initiate",
                      label:
                        "Initiate Login URI (for IdP-initiated / app-tile login)",
                      value:
                        spInfoConfig.oidc?.rp?.initiateUri ??
                        `${urls.OIDC_INITIATE_URI_BASE}/${spInfoConfig.registrationId}`,
                    },
                  ]
                : [
                    {
                      key: "acs",
                      label: "ACS URL / Reply URL / Single Sign-On URL",
                      value:
                        spInfoConfig.saml?.sp?.acsUrl ??
                        `${urls.SAML_SP_ACS}/${spInfoConfig.registrationId}`,
                    },
                    {
                      key: "entity",
                      label:
                        "SP Entity ID / Audience URI / Metadata URL (import instead of typing manually)",
                      value:
                        spInfoConfig.saml?.sp?.entityId ??
                        spInfoConfig.saml?.sp?.metadataUrl ??
                        `${urls.SAML_SP_METADATA}/${spInfoConfig.registrationId}`,
                    },
                    {
                      key: "slo",
                      label:
                        "SP Single Logout URL (only needed if entered manually)",
                      value: spInfoConfig.saml?.sp?.sloUrl ?? urls.SAML_SP_SLO,
                    },
                  ]
              ).map(({ key, label, value }) => (
                <Box key={key}>
                  <Typography sx={valueLabelSx}>{label}</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography sx={valueTextSx}>{value}</Typography>
                    <Tooltip title={copiedField === key ? "Copied!" : "Copy"}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          navigator.clipboard.writeText(value);
                          setCopiedField(key);
                          setTimeout(() => setCopiedField(null), 2000);
                        }}
                        sx={{ flexShrink: 0 }}
                      >
                        {copiedField === key ? (
                          <DoneAllIcon
                            sx={{ fontSize: 16, color: "success.main" }}
                          />
                        ) : (
                          <ContentCopyIcon
                            sx={{ fontSize: 16, color: "text.secondary" }}
                          />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ))}
              {isOidcConfig(spInfoConfig) && (
                <Typography variant="caption" color="text.secondary">
                  If your IdP only supports a single, shared "Initiate Login
                  URI" across apps (e.g. Okta), use{" "}
                  <span style={{ fontFamily: "monospace" }}>
                    {spInfoConfig.oidc?.rp?.sharedInitiateUri ??
                      urls.OIDC_INITIATE_URI_BASE}
                  </span>{" "}
                  instead (no registration id suffix) — it resolves the
                  connector from the issuer the IdP sends, and only works if
                  this issuer isn't shared by another SSO connector on this
                  account.
                </Typography>
              )}
            </Stack>
          </AppModal>
        )}

        {isApiLoading && <CustomLoader isLoading={isApiLoading} />}
      </Stack>
    </SettingsLayout>
  );
};

export default EnterpriseSSO;
